import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import { Plus, Trash2, Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { DatePicker } from "../components/DatePicker";

/**
 * Public supplier portal — token-gated, decoupled from the pricing app.
 *
 * Renders the supplier name, a 10-row manual table, and a file-upload area.
 * No navigation, no shared header, no access to the rest of the application.
 */

const MAX_ROWS = 10;
const BLANK_ROW = { sku: "", description: "", price: "", effective_date: "", effective_to: "" };

// Static currency display map (mirrors backend ALLOWED_CURRENCIES). The
// portal fetches the live list from /api/portal/currencies on mount; this
// map is just the symbol resolver used inside the table headers.
const CURRENCY_SYMBOLS = {
  GBP: "£", EUR: "€", USD: "$", AUD: "A$",
  CAD: "C$", NZD: "NZ$", CHF: "CHF ", JPY: "¥",
};

export default function Portal() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [phase, setPhase] = useState("loading"); // loading | invalid | form | submitted
  const [supplier, setSupplier] = useState(null);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [currencies, setCurrencies] = useState([{ code: "GBP", symbol: "£" }]);

  // Validate token + load supplier name
  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }
    axios.get(`${API}/portal/supplier`, { params: { token } })
      .then(r => { setSupplier(r.data); setPhase("form"); })
      .catch(() => setPhase("invalid"));
  }, [token]);

  // Currencies are static metadata — fetch once, ignore failure (we fall
  // back to GBP-only which is the supplier-portal default anyway).
  useEffect(() => {
    axios.get(`${API}/portal/currencies`)
      .then(r => setCurrencies(r.data?.currencies || [{ code: "GBP", symbol: "£" }]))
      .catch(() => {});
  }, []);

  if (phase === "loading") {
    return <PortalShell><p style={{ opacity: 0.7 }}>Loading…</p></PortalShell>;
  }
  if (phase === "invalid") {
    return (
      <PortalShell>
        <div data-testid="portal-invalid-token" style={{ textAlign: "center", padding: "2rem 0" }}>
          <AlertTriangle size={32} style={{ color: "#F050F8", marginBottom: "1rem" }} />
          <p style={{ color: "var(--octopus-ice)", fontSize: "1.05rem" }}>
            This link is not recognised. Please contact your Octopus Energy account manager.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (phase === "submitted") {
    return (
      <PortalShell supplierName={supplier?.supplier_name}>
        <SubmittedView token={token} submission={submission} onReset={() => { setSubmission(null); setPhase("form"); }} />
      </PortalShell>
    );
  }

  return (
    <PortalShell supplierName={supplier?.supplier_name} category={supplier?.category}>
      <ManualEntrySection token={token} currencies={currencies} onSuccess={(s) => { setSubmission(s); setPhase("submitted"); }} />
      <Divider label="Updating more than 10 SKUs?" />
      <FileUploadSection token={token} currencies={currencies} onSuccess={(s) => { setSubmission(s); setPhase("submitted"); }} />
      {error && <p style={{ color: "#F050F8", marginTop: "1rem" }}>{error}</p>}
    </PortalShell>
  );
}


// ─── Shell (used in every phase) ────────────────────────────────────────────

function PortalShell({ children, supplierName, category }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #0A122A 0%, #0F1838 100%)",
      padding: "2.5rem 1.5rem",
      color: "var(--octopus-ice)",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }} data-testid="portal-page">
      <div style={{ maxWidth: "920px", margin: "0 auto" }}>
        <header style={{ marginBottom: "2.5rem", borderBottom: "1px solid rgba(96,240,248,0.18)", paddingBottom: "1.5rem" }}>
          <p style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "var(--octopus-voltage)", opacity: 0.85, marginBottom: "0.5rem", textTransform: "uppercase" }}>
            Octopus Energy · Supplier Price Update Portal
          </p>
          {supplierName && (
            <h1 data-testid="portal-supplier-name" style={{ fontSize: "1.75rem", fontWeight: "700", margin: 0, color: "var(--octopus-ice)" }}>
              {supplierName} {category && <span style={{ fontSize: "0.85rem", fontWeight: "400", color: "var(--octopus-voltage)", marginLeft: "0.5rem" }}>· {category}</span>}
            </h1>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}


function Divider({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem", margin: "3rem 0 2rem" }}>
      <div style={{ flex: 1, height: "1px", background: "rgba(96,240,248,0.15)" }} />
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", margin: 0, fontWeight: "500" }}>{label}</p>
      <div style={{ flex: 1, height: "1px", background: "rgba(96,240,248,0.15)" }} />
    </div>
  );
}


// ─── Manual entry section ───────────────────────────────────────────────────

function ManualEntrySection({ token, currencies, onSuccess }) {
  const [rows, setRows] = useState(() => [{ ...BLANK_ROW }, { ...BLANK_ROW }, { ...BLANK_ROW }]);
  const [notes, setNotes] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [fastTrackToken, setFastTrackToken] = useState("");
  const [rowErrors, setRowErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const currencySymbol = CURRENCY_SYMBOLS[currency] || currency;

  const updateRow = (idx, field, value) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    setRowErrors(e => ({ ...e, [`${idx}.${field}`]: null }));
  };

  const removeRow = (idx) => {
    if (rows.length === 1) {
      setRows([{ ...BLANK_ROW }]);
    } else {
      setRows(rs => rs.filter((_, i) => i !== idx));
    }
  };

  const addRow = () => {
    if (rows.length < MAX_ROWS) setRows(rs => [...rs, { ...BLANK_ROW }]);
  };

  // Paste from Excel: detect tab/newline → fill rows
  const handlePaste = (idx, e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, "").split("\n").filter(l => l.trim());
    const capped = lines.slice(0, MAX_ROWS);
    const newRows = capped.map(line => {
      const cells = line.split("\t");
      return {
        sku: (cells[0] || "").trim(),
        description: (cells[1] || "").trim(),
        price: (cells[2] || "").trim(),
        effective_date: (cells[3] || "").trim(),
        effective_to: (cells[4] || "").trim(),
      };
    });
    if (lines.length > MAX_ROWS) {
      toast.info("Maximum 10 rows. For larger updates, use the file upload below.");
    }
    setRows(newRows);
  };

  const handleSubmit = async () => {
    const filled = rows.filter(r => r.sku.trim() || r.price.trim());
    if (filled.length === 0) {
      toast.error("Add at least one row with a SKU and price.");
      return;
    }
    // Client-side validation
    const errs = {};
    filled.forEach((r, i) => {
      if (!r.sku.trim()) errs[`${i}.sku`] = "Required";
      if (r.sku.length > 50) errs[`${i}.sku`] = "Max 50 chars";
      const priceNum = parseFloat(r.price);
      if (!r.price.trim() || isNaN(priceNum)) errs[`${i}.price`] = "Required";
      else if (priceNum <= 0) errs[`${i}.price`] = "Must be positive";
      if (!r.effective_date) errs[`${i}.effective_date`] = "Required";
      // Backdating allowed: effective_date may be in the past.
      // effective_to is optional; if set, must be >= effective_date
      if (r.effective_to && r.effective_date && r.effective_to < r.effective_date) {
        errs[`${i}.effective_to`] = "Must be on or after Effective Date";
      }
    });
    if (Object.keys(errs).length > 0) {
      setRowErrors(errs);
      toast.error("Please fix the highlighted cells.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        rows: filled.map(r => ({
          sku: r.sku.trim(),
          product_description: r.description.trim() || undefined,
          new_price: parseFloat(r.price),
          effective_date: r.effective_date,
          effective_to: r.effective_to || undefined,
        })),
        notes: notes.trim() || undefined,
        currency,
        fast_track_token: fastTrackToken.trim().toLowerCase() || undefined,
      };
      const res = await axios.post(`${API}/portal/submit-manual?token=${encodeURIComponent(token)}`, payload);
      onSuccess({ ...res.data, type: "manual" });
    } catch (err) {
      const detail = err.response?.data?.detail || "Submission failed";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section style={{ marginBottom: "1rem" }} data-testid="manual-entry-section">
      <h2 style={{ fontSize: "1.05rem", marginBottom: "1rem", fontWeight: "600", color: "var(--octopus-ice)" }}>
        Enter up to 10 SKUs
      </h2>
      <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <label style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)" }} htmlFor="manual-currency">
          Currency for this submission
        </label>
        <select
          id="manual-currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          data-testid="manual-currency"
          style={{
            padding: "0.4rem 0.7rem", borderRadius: "6px",
            background: "rgba(10,18,42,0.6)", border: "1px solid rgba(96,240,248,0.25)",
            color: "var(--octopus-ice)", fontSize: "0.9rem", minWidth: "130px",
          }}
        >
          {currencies.map(c => (
            <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
          ))}
        </select>
        <span style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.65 }}>
          Defaults to £ GBP. Prices are stored and displayed in the chosen currency — no conversion.
        </span>
      </div>
      <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid rgba(96,240,248,0.15)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead style={{ background: "rgba(96,240,248,0.06)" }}>
            <tr>
              <Th>SKU *</Th>
              <Th>Description</Th>
              <Th width="120px">Price ({currencySymbol}) *</Th>
              <Th width="160px">Effective From *</Th>
              <Th width="160px">Effective To</Th>
              <Th width="40px" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <Td><Input value={r.sku} onChange={v => updateRow(idx, "sku", v)} onPaste={(e) => handlePaste(idx, e)} error={rowErrors[`${idx}.sku`]} testId={`row-${idx}-sku`} /></Td>
                <Td><Input value={r.description} onChange={v => updateRow(idx, "description", v)} testId={`row-${idx}-desc`} /></Td>
                <Td><Input value={r.price} type="number" step="0.01" onChange={v => updateRow(idx, "price", v)} error={rowErrors[`${idx}.price`]} testId={`row-${idx}-price`} /></Td>
                <Td>
                  <DatePicker
                    value={r.effective_date}
                    onChange={v => updateRow(idx, "effective_date", v)}
                    error={rowErrors[`${idx}.effective_date`]}
                    testId={`row-${idx}-date`}
                  />
                </Td>
                <Td>
                  <DatePicker
                    value={r.effective_to}
                    onChange={v => updateRow(idx, "effective_to", v)}
                    minDate={r.effective_date ? new Date(r.effective_date) : new Date(new Date().setHours(0, 0, 0, 0))}
                    error={rowErrors[`${idx}.effective_to`]}
                    testId={`row-${idx}-date-to`}
                    placeholder="Open-ended"
                  />
                </Td>
                <Td>
                  <button onClick={() => removeRow(idx)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--octopus-voltage)", opacity: 0.6 }} title="Remove row" data-testid={`row-${idx}-remove`}>
                    <Trash2 size={14} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length < MAX_ROWS && (
        <button
          onClick={addRow}
          data-testid="add-row-btn"
          style={{
            marginTop: "0.75rem", padding: "0.4rem 0.9rem", background: "transparent",
            border: "1px dashed rgba(96,240,248,0.4)", borderRadius: "6px",
            color: "var(--octopus-voltage)", cursor: "pointer", fontSize: "0.85rem",
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
          }}
        >
          <Plus size={14} /> Add row
        </button>
      )}

      <div style={{ marginTop: "1.25rem" }}>
        <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--octopus-voltage)" }}>
          Notes (optional, max 500 chars)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 500))}
          rows={2}
          data-testid="portal-notes"
          style={{
            width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px",
            background: "rgba(10,18,42,0.6)", border: "1px solid rgba(96,240,248,0.2)",
            color: "var(--octopus-ice)", fontSize: "0.9rem", resize: "vertical",
          }}
        />
      </div>

      <FastTrackTokenField value={fastTrackToken} onChange={setFastTrackToken} testId="manual-fast-track-token" />

      <Button onClick={handleSubmit} disabled={submitting} className="btn btn-primary" data-testid="portal-submit-manual" style={{ marginTop: "1.25rem" }}>
        {submitting ? "Submitting…" : (fastTrackToken.trim() ? "Submit & Auto-Approve" : "Submit Price Update")}
      </Button>
    </section>
  );
}


// ─── File upload section ────────────────────────────────────────────────────

const DETECT_FIELDS = [["sku", "SKU"], ["price", "New price"], ["effective_date", "Effective date"], ["effective_to", "Effective to"], ["description", "Description"], ["notes", "Notes"]];

function FileUploadSection({ token, currencies, onSuccess }) {
  const [file, setFile] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [notes, setNotes] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [fastTrackToken, setFastTrackToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [detect, setDetect] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const fileInputRef = useRef(null);

  const runDetect = async (f) => {
    setDetecting(true);
    setDetect(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/portal/submit-detect?token=${encodeURIComponent(token)}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setDetect(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that file.");
      setDetect(null);
    } finally { setDetecting(false); }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10MB limit.");
      return;
    }
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".csv") && !lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      toast.error("Only .csv and .xlsx files are accepted.");
      return;
    }
    setFile(f);
    runDetect(f);
  };

  const needDate = detect && !detect.hasDates;
  const detectOk = detect && detect.mapping?.sku && detect.mapping?.price && detect.rowCount > 0;

  const handleSubmit = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    if (needDate && !effectiveDate) { toast.error("This file has no dates, so pick an effective date for it."); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const ft = fastTrackToken.trim().toLowerCase();
      const url = `${API}/portal/submit-bulk?token=${encodeURIComponent(token)}${effectiveDate ? "&effective_date=" + effectiveDate : ""}&currency=${encodeURIComponent(currency)}${notes ? "&notes=" + encodeURIComponent(notes) : ""}${ft ? "&fast_track_token=" + encodeURIComponent(ft) : ""}`;
      const res = await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      onSuccess({ ...res.data, type: "bulk" });
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = () => {
    window.location.href = `${API}/portal/template`;
  };

  return (
    <section data-testid="file-upload-section">
      <Button onClick={downloadTemplate} className="btn btn-secondary" data-testid="download-template-btn" style={{ marginBottom: "1rem" }}>
        <Download size={14} style={{ marginRight: "0.4rem" }} /> Download price update template (CSV)
      </Button>

      <div style={{
        padding: "0.85rem 1rem", marginBottom: "1.25rem", borderRadius: "6px",
        background: "rgba(96,240,248,0.05)", border: "1px solid rgba(96,240,248,0.15)",
        fontSize: "0.8rem", color: "var(--octopus-voltage)", lineHeight: "1.5",
      }}>
        <strong style={{ color: "var(--octopus-ice)" }}>How it works</strong>
        <ul style={{ margin: "0.4rem 0 0 1.2rem", padding: 0 }}>
          <li>Accepted: CSV or .xlsx, max 10MB. No fixed template needed.</li>
          <li>We detect your columns automatically: the product code/SKU, the new price, and the effective date. You confirm before it submits.</li>
          <li>Include an effective-date column and each row can take effect on its own date. No date column? Supply one date for the whole file below.</li>
          <li>An "effective to" / "valid to" column is optional (blank means open-ended).</li>
          <li>Prices must be numeric with no currency symbol. Pick the currency below.</li>
          <li>Row 1 should be the header row, with no totals or blank rows above it.</li>
        </ul>
      </div>

      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        onDragOver={(e) => e.preventDefault()}
        style={{
          padding: "1.5rem", borderRadius: "8px", border: "2px dashed rgba(96,240,248,0.25)",
          textAlign: "center", cursor: "pointer", background: file ? "rgba(24,240,184,0.06)" : "rgba(10,18,42,0.4)",
          marginBottom: "1rem",
        }}
        data-testid="file-drop-zone"
      >
        <Upload size={20} style={{ color: "var(--octopus-voltage)", marginBottom: "0.4rem" }} />
        <p style={{ margin: "0.3rem 0", color: "var(--octopus-ice)", fontSize: "0.9rem" }}>
          {file ? <><FileSpreadsheet size={14} style={{ display: "inline", marginRight: "0.3rem" }} />{file.name} ({(file.size / 1024).toFixed(0)} KB)</> : "Drop a file here, or click to browse"}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="file-input"
        />
      </div>

      {detecting && (
        <div style={{ padding: "0.85rem 1rem", marginBottom: "1.25rem", borderRadius: 6, background: "rgba(10,18,42,0.4)", border: "1px solid rgba(96,240,248,0.15)", fontSize: "0.85rem", color: "var(--octopus-voltage)" }}>Reading your file…</div>
      )}

      {detect && (
        <div style={{ padding: "1rem 1.1rem", marginBottom: "1.5rem", borderRadius: 8, background: "rgba(10,18,42,0.5)", border: `1px solid ${detectOk ? "rgba(24,240,184,0.3)" : "rgba(255,120,120,0.35)"}` }} data-testid="detect-preview">
          <strong style={{ color: "var(--octopus-ice)", fontSize: "0.9rem" }}>We detected these columns. Please check before submitting.</strong>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "0.4rem 1rem", margin: "0.7rem 0" }}>
            {DETECT_FIELDS.map(([k, label]) => (
              <div key={k} style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)" }}>
                {label}: {detect.mapping?.[k]
                  ? <span style={{ color: "var(--octopus-ice)", fontWeight: 600 }}>{detect.mapping[k]}</span>
                  : <span style={{ color: (k === "sku" || k === "price") ? "#fb7185" : "#7c8698" }}>{(k === "sku" || k === "price") ? "not found" : "none"}</span>}
              </div>
            ))}
          </div>
          <div style={{ fontSize: "0.82rem", color: "var(--octopus-ice)", marginBottom: "0.4rem" }}>
            {detect.hasDates ? "Effective dates read from the file (per row)." : "No date column found. Supply one effective date below and it applies to the whole file."}
            {" "}<strong>{detect.rowCount}</strong> row{detect.rowCount === 1 ? "" : "s"} ready{detect.skippedCount ? <>, <span style={{ color: "#fbbf24" }}>{detect.skippedCount} will be skipped</span></> : ""}.
          </div>
          {(detect.warnings || []).map((w, i) => (
            <div key={i} style={{ fontSize: "0.8rem", color: "#fbbf24", marginTop: 2 }}>{w}</div>
          ))}
          {(detect.sample || []).length > 0 && (
            <div style={{ marginTop: "0.6rem", overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", width: "100%" }}>
                <thead><tr>{["SKU", "New price", "Effective", "To"].map((h) => <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "var(--octopus-voltage)", borderBottom: "1px solid rgba(96,240,248,0.15)" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {detect.sample.slice(0, 5).map((r, i) => (
                    <tr key={i}>
                      <td style={{ padding: "4px 8px", color: "var(--octopus-ice)", fontFamily: "monospace" }}>{r.sku}</td>
                      <td style={{ padding: "4px 8px", color: "var(--octopus-ice)" }}>{r.new_price}</td>
                      <td style={{ padding: "4px 8px", color: r.effective_date ? "var(--octopus-ice)" : "#7c8698" }}>{r.effective_date || "(from date below)"}</td>
                      <td style={{ padding: "4px 8px", color: "var(--octopus-voltage)" }}>{r.effective_to || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "1rem", marginBottom: "2rem" }}>
        <div>
          <label style={{ display: "block", marginBottom: "0.3rem", fontSize: "0.85rem", color: "var(--octopus-voltage)" }}>
            {needDate ? "Effective Date * (file has no dates)" : "Effective Date (fallback, optional)"}
          </label>
          <DatePicker
            value={effectiveDate}
            onChange={setEffectiveDate}
            testId="bulk-effective-date"
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: "0.3rem", fontSize: "0.85rem", color: "var(--octopus-voltage)" }} htmlFor="bulk-currency">
            Currency *
          </label>
          <select
            id="bulk-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            data-testid="bulk-currency"
            style={{
              width: "100%", padding: "0.5rem 0.7rem", borderRadius: "6px",
              background: "rgba(10,18,42,0.6)", border: "1px solid rgba(96,240,248,0.25)",
              color: "var(--octopus-ice)", fontSize: "0.9rem",
            }}
          >
            {currencies.map(c => (
              <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", marginBottom: "0.3rem", fontSize: "0.85rem", color: "var(--octopus-voltage)" }}>
            Notes (optional, max 500 chars)
          </label>
          <Input value={notes} onChange={v => setNotes(v.slice(0, 500))} testId="bulk-notes" />
        </div>
      </div>

      <FastTrackTokenField value={fastTrackToken} onChange={setFastTrackToken} testId="bulk-fast-track-token" />

      <Button onClick={handleSubmit} disabled={submitting || !file || detecting || (detect && !detectOk)} className="btn btn-primary" data-testid="portal-submit-bulk">
        {submitting ? "Uploading…" : detecting ? "Reading file…" : (fastTrackToken.trim() ? "Confirm & Auto-Approve" : "Confirm & Upload")}
      </Button>
    </section>
  );
}


/**
 * Optional one-time fast-track token field. When the supplier pastes a
 * valid token, their submission is auto-approved and routed straight to
 * the records updater (no PM review). Invalid tokens fail the submission
 * up-front so the supplier can either correct the value or clear it.
 *
 * Visually de-emphasised (collapsed by default) — most submissions go
 * through standard review and we don't want this to become friction.
 */
function FastTrackTokenField({ value, onChange, testId }) {
  const [open, setOpen] = useState(!!value);
  return (
    <div style={{ marginTop: "1rem" }} data-testid={`${testId}-wrapper`}>
      {!open && !value ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-reveal`}
          style={{
            background: "transparent", border: "1px dashed rgba(255,216,79,0.35)",
            color: "#FFD84F", padding: "0.4rem 0.8rem", borderRadius: "999px",
            fontSize: "0.78rem", cursor: "pointer", fontWeight: 600,
          }}
        >
          ⚡ Got a fast-track token?
        </button>
      ) : (
        <div style={{
          padding: "0.75rem 0.9rem", borderRadius: "8px",
          background: "rgba(255,216,79,0.06)", border: "1px solid rgba(255,216,79,0.3)",
        }}>
          <label style={{ display: "block", marginBottom: "0.3rem", fontSize: "0.78rem", color: "#FFD84F", fontWeight: 600, letterSpacing: "0.02em" }}>
            ⚡ Fast-track token <span style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontWeight: 400 }}>· optional · auto-approves your submission</span>
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g. dog-carrot-python"
            data-testid={testId}
            style={{
              width: "100%", padding: "0.5rem 0.7rem", borderRadius: "6px",
              background: "rgba(10,18,42,0.6)", border: "1px solid rgba(96,240,248,0.2)",
              color: "var(--octopus-ice)", fontSize: "0.9rem", fontFamily: "monospace",
              letterSpacing: "0.02em",
            }}
          />
          <p style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", opacity: 0.7, margin: "0.4rem 0 0" }}>
            Paste the one-time token your account manager sent you. Leave blank to send for standard review.
          </p>
        </div>
      )}
    </div>
  );
}


// ─── Submitted view ─────────────────────────────────────────────────────────

function SubmittedView({ token, submission, onReset }) {
  const downloadErrorReport = useCallback(() => {
    window.location.href = `${API}/portal/error-report/${submission.reference_id}?token=${encodeURIComponent(token)}`;
  }, [token, submission.reference_id]);

  const hasSkipped = submission.rows_skipped > 0;
  const autoApproved = !!submission.auto_approved;
  return (
    <div style={{ textAlign: "center", padding: "2rem 0" }} data-testid="portal-submitted">
      <CheckCircle size={36} style={{ color: autoApproved ? "#FFD84F" : "var(--octopus-green)", marginBottom: "1rem" }} />
      <h2 style={{ fontSize: "1.4rem", marginBottom: "0.5rem", color: "var(--octopus-ice)" }}>
        {autoApproved
          ? "Fast-tracked ⚡ — auto-approved."
          : submission.type === "bulk" ? "File received." : "Price update submitted."}
      </h2>
      <p style={{ color: "var(--octopus-voltage)", marginBottom: "0.4rem" }}>
        {submission.type === "bulk"
          ? `${submission.rows_imported.toLocaleString()} rows imported${hasSkipped ? `, ${submission.rows_skipped} rows skipped` : ""}.`
          : `${submission.rows_imported} row(s) submitted.`}
      </p>
      {autoApproved && (
        <p data-testid="portal-auto-approved-banner" style={{ color: "#FFD84F", fontSize: "0.85rem", marginBottom: "0.4rem", fontWeight: 500 }}>
          Routed directly to the records updater — no PM review needed.
        </p>
      )}
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", opacity: 0.75, marginBottom: "1.5rem" }}>
        Reference: <strong style={{ color: "var(--octopus-ice)", fontFamily: "monospace" }}>{submission.reference_id}</strong>
      </p>
      {hasSkipped && (
        <Button onClick={downloadErrorReport} className="btn btn-secondary" data-testid="download-error-report-btn" style={{ marginBottom: "1rem" }}>
          <Download size={14} style={{ marginRight: "0.4rem" }} /> Download error report
        </Button>
      )}
      <p style={{ color: "var(--octopus-voltage)", fontSize: "0.9rem", marginTop: "1rem" }}>
        Your Procurement contact will be in touch if further information is needed.
        {hasSkipped && <><br />Review the error report and resubmit corrected rows.</>}
      </p>
      <button onClick={onReset} style={{ background: "transparent", border: "none", color: "var(--octopus-voltage)", marginTop: "1.5rem", cursor: "pointer", textDecoration: "underline" }} data-testid="portal-new-submission">
        Submit another update
      </button>
    </div>
  );
}


// ─── Tiny inline primitives (keep portal self-contained) ────────────────────

function Th({ children, width }) {
  return <th style={{ padding: "0.6rem 0.5rem", textAlign: "left", fontSize: "0.75rem", color: "var(--octopus-voltage)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em", width }}>{children}</th>;
}

function Td({ children }) {
  return <td style={{ padding: "0.35rem 0.5rem", borderTop: "1px solid rgba(96,240,248,0.08)" }}>{children}</td>;
}

function Input({ value, onChange, onPaste, type = "text", step, min, error, testId }) {
  return (
    <>
      <input
        type={type}
        step={step}
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        data-testid={testId}
        style={{
          width: "100%", padding: "0.4rem 0.6rem", border: error ? "1px solid #F050F8" : "1px solid rgba(96,240,248,0.15)",
          borderRadius: "4px", background: "rgba(10,18,42,0.6)", color: "var(--octopus-ice)", fontSize: "0.9rem",
        }}
      />
      {error && <p style={{ color: "#F050F8", fontSize: "0.7rem", margin: "0.2rem 0 0" }}>{error}</p>}
    </>
  );
}
